import type { ComponentProps, MouseEvent } from "react"
import { flexRender, type Cell } from "@tanstack/react-table"
import cn from "@/v2-ui/utils/utils.cn"
import { useTableContext } from "./table.context"
import { SPACER_COLUMN_ID } from "./table.constants"
import { useOverflowPopover } from "./table.overflow"
import { getColumnSizeStyle, isInternalColumnId } from "./table.utils"

function handleCellClick(e: MouseEvent) {
  e.stopPropagation()
}

type ColumnCellMeta = {
  isWrapped?: boolean
  className?: string
}

export type TableBodyCellProps<TData = unknown> = ComponentProps<"td"> & {
  cell: Cell<TData, unknown>
  isHovered: boolean
}

function TableBodyCell<TData = unknown>(props: TableBodyCellProps<TData>) {
  const { cell, isHovered, className, ...tdProps } = props
  const { isCellsWrapped, table, rowToolbarRef, hasPersistence } =
    useTableContext()
  const overflow = useOverflowPopover<HTMLTableCellElement>()

  const meta = cell.column.columnDef.meta as ColumnCellMeta | undefined
  const columnId = cell.column.id
  const isInternalColumn = isInternalColumnId(columnId)
  const isSpacer = columnId === SPACER_COLUMN_ID
  const isWrapped = meta?.isWrapped ?? isCellsWrapped
  const columnClassName = meta?.className
  const style = getColumnSizeStyle({
    column: cell.column,
    columnSizing: table.getState().columnSizing,
    hasPersistence,
  })

  if (isSpacer) {
    return <td className="ui-table-spacer-cell" style={style} {...tdProps} />
  }

  const toolbar = rowToolbarRef.current
  const showToolbar =
    isHovered && toolbar !== null && toolbar.column === cell.column.id
  // Wrapped cells show everything already; internal cells hold controls.
  const canRevealOverflow = !isWrapped && !isInternalColumn

  if (cell.getIsPlaceholder() || cell.getIsAggregated()) {
    return (
      <td
        className={cn("ui-table-body-cell", columnClassName, className)}
        style={style}
        {...tdProps}
      />
    )
  }

  const cellContent = flexRender(cell.column.columnDef.cell, cell.getContext())

  return (
    <td
      ref={overflow.ref}
      className={cn(
        "ui-table-body-cell",
        !isInternalColumn &&
          (isWrapped
            ? "ui-table-body-cell-is-wrapped"
            : "ui-table-body-cell-is-truncated"),
        { "ui-table-body-cell-has-toolbar": showToolbar },
        columnClassName,
        className,
      )}
      style={style}
      onMouseEnter={canRevealOverflow ? overflow.onMouseEnter : undefined}
      onMouseLeave={overflow.onMouseLeave}
      onClick={isInternalColumn ? handleCellClick : undefined}
      {...tdProps}
    >
      {cellContent}
      {showToolbar && (
        <div
          className="ui-table-row-toolbar"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          {toolbar.render(cell.row.original)}
        </div>
      )}
      {overflow.popoverStyle && (
        <div className="ui-table-cell-popover" style={overflow.popoverStyle}>
          {cellContent}
        </div>
      )}
    </td>
  )
}

export default TableBodyCell
